/**
 * QuotePresetFormModal — legacy's preset editor (quote_details_new.php?admin=1,
 * opened from admin/admin_quote_template_list.php): the preset's Name, then
 * the Procedures, Products (legacy's "Items") and Discounts that Load Preset
 * copies onto a patient's quote. Legacy's preset editor has no facility,
 * financing, totals, Active or Locked, so neither does this.
 *
 * The three lists look and work like the Products and Procedures of the
 * Membership and Package modals (TemplateItemRows): each line is an accordion
 * row (red while nothing is picked) that opens to its fields.
 *   - A procedure: Category → Sub Category → Procedure with the Description
 *     beside them, then Hours, Normal and Effective price. Picking it fills in
 *     both prices, the Hours band, the Description, and the procedure's own
 *     anesthesia and OR fees (which a quote line carries).
 *   - A product: Category and Product with the Description, then Cost,
 *     Quantity and Taxable beside them. Picking it fills in its name, unit,
 *     cost and Taxable, as the quote editor does.
 *   - A discount: the Discount and its Description, then Amount, Type and
 *     Applies to. Picking it fills in the rest from the catalog.
 *   - A line on an entry since retired keeps it as a choice, marked inactive.
 *   - Hours come from the account's largest facility scale, as legacy's do (a
 *     preset has no facility). No tax is shown: it is worked out on the
 *     patient's quote, at its facility's rate.
 *
 * No Category field: a preset is added from its category's row and moved by
 * dragging it on the board (as the other Admin boards). An edit loads the
 * preset's quote first; a preset with no quote, or whose quote is gone, starts
 * empty and its first save gives it one. Save sends the name and each list
 * only when changed, and is held while a line has nothing picked (as the
 * membership form). Edits are tracked (Revert, the discard prompt). On a
 * Locked page AdminFormModal makes it a viewer: lines still open to read.
 */
import { useEffect, useState } from 'react';
import { isAxiosError } from 'axios';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import {
  DEFAULT_DISCOUNT_IS_PERCENT,
  DEFAULT_DISCOUNT_SCOPE,
  DISCOUNT_SCOPE_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
} from '@/pages/quotes/discountOptions';
import { getErrorMessage } from '@/services/apiClient';
import { listProcedureTree, type ProcedureCategoryOption } from '@/services/membershipTemplates.service';
import {
  getDiscountCatalog,
  getProductCatalog,
  getQuote,
  type CatalogDiscount,
  type CatalogProduct,
  type Quote,
  type QuoteDiscount,
  type QuoteItem,
  type QuoteProcedure,
} from '@/services/quotes.service';
import {
  createQuotePreset,
  getQuotePresetHourBands,
  updateQuotePreset,
  type QuotePreset,
  type QuotePresetContent,
} from '@/services/quotePresetsAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DialogField, fieldClasses, type FieldState } from './DialogChrome';
import { ItemRow, ItemsBox, ProcedureFields, procedureNameOf, type ProcedureFieldsRow } from './TemplateItemRows';
import { useTrackedForm } from './useTrackedForm';

// ─── Form ──────────────────────────────────────────────────────────────────────

interface PresetProcedureRow extends ProcedureFieldsRow {
  planProcedureId: number | null;
  anesthesiaFee: number;
  operatingRoomFee: number;
}

interface PresetProductRow {
  key: string;
  quoteItemId: number | null;
  costItemId: number | null;
  /** The catalog category, narrowing the Product list. */
  category: string;
  itemName: string;
  itemType: string;
  unit: string;
  description: string;
  curRate: string;
  quantity: string;
  taxable: boolean;
}

interface PresetDiscountRow {
  key: string;
  quoteDiscountId: number | null;
  discountId: number | null;
  name: string;
  description: string;
  amount: string;
  isPercent: boolean;
  quoteElement: string;
  active: boolean;
}

interface QuotePresetForm {
  name: string;
  procedures: PresetProcedureRow[];
  products: PresetProductRow[];
  discounts: PresetDiscountRow[];
}

interface Options {
  procedureTree: ProcedureCategoryOption[];
  /** The Hours choices (above 0) for a procedure line. */
  hourBands: number[];
  products: CatalogProduct[];
  discounts: CatalogDiscount[];
}

const UNCATEGORIZED = 'Uncategorized';
const LISTS = ['procedures', 'products', 'discounts'] as const;

let rowSeq = 0;
const nextKey = () => `preset-row-${++rowSeq}`;

const money = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const categoryOf = (p: CatalogProduct) => p.categoryName?.trim() || UNCATEGORIZED;

function formFrom(preset: QuotePreset | null, quote: Quote | null, options: Options | null): QuotePresetForm {
  return {
    name: preset?.name ?? '',
    procedures: (quote?.procedures ?? []).map((p) => ({
      key: `procedure-${p.planProcedureId}`,
      planProcedureId: p.planProcedureId,
      categoryID: p.categoryId,
      procID: p.procId,
      procSubID: p.procSubId,
      savedName: p.specificName ?? '',
      savedCategoryName: p.categoryName ?? '',
      savedSubcategoryName: p.subName ?? '',
      description: p.laymansTerms ?? '',
      // Legacy stores the string 'null' for no hours: anything not a number is 0.
      hours: Number(p.expNumHours) || 0,
      price: String(p.price ?? 0),
      effectivePrice: String(p.effectivePrice ?? 0),
      anesthesiaFee: p.anesthesiaFee ?? 0,
      operatingRoomFee: p.operatingRoomFee ?? 0,
    })),
    products: (quote?.items ?? []).map((i) => {
      const catalog = options?.products.find((p) => p.costItemId === i.costItemId);
      return {
        key: `product-${i.quoteItemId}`,
        quoteItemId: i.quoteItemId,
        costItemId: i.costItemId,
        category: catalog ? categoryOf(catalog) : '',
        itemName: i.itemName ?? '',
        itemType: i.itemType ?? '',
        unit: i.unit ?? '',
        description: i.description ?? '',
        curRate: String(i.curRate ?? 0),
        quantity: String(i.quantity || 1),
        taxable: i.taxable,
      };
    }),
    discounts: (quote?.discounts ?? []).map((d) => ({
      key: `discount-${d.quoteDiscountId}`,
      quoteDiscountId: d.quoteDiscountId,
      discountId: d.discountId || null,
      name: d.name ?? '',
      description: d.description ?? '',
      amount: String(d.amount ?? 0),
      isPercent: d.isPercent,
      quoteElement: d.quoteElement || DEFAULT_DISCOUNT_SCOPE,
      active: d.active,
    })),
  };
}

/** The lists in the shape the quote save takes; a stored line keeps its id. */
function contentPayload(f: QuotePresetForm): QuotePresetContent {
  return {
    procedures: f.procedures.map(
      (r): Partial<QuoteProcedure> => ({
        ...(r.planProcedureId ? { planProcedureId: r.planProcedureId } : {}),
        categoryId: r.categoryID,
        procId: r.procID,
        procSubId: r.procSubID,
        laymansTerms: r.description,
        price: Number(r.price) || 0,
        effectivePrice: Number(r.effectivePrice) || 0,
        expNumHours: r.hours,
        anesthesiaFee: r.anesthesiaFee,
        operatingRoomFee: r.operatingRoomFee,
      }),
    ),
    items: f.products.map(
      (r): Partial<QuoteItem> => ({
        ...(r.quoteItemId ? { quoteItemId: r.quoteItemId } : {}),
        costItemId: r.costItemId,
        itemName: r.itemName,
        itemType: r.itemType,
        unit: r.unit,
        description: r.description,
        curRate: Number(r.curRate) || 0,
        quantity: Math.max(1, Math.round(Number(r.quantity) || 1)),
        taxable: r.taxable,
      }),
    ),
    discounts: f.discounts.map(
      (r): Partial<QuoteDiscount> => ({
        ...(r.quoteDiscountId ? { quoteDiscountId: r.quoteDiscountId } : {}),
        discountId: r.discountId ?? 0,
        name: r.name,
        description: r.description,
        amount: Math.max(0, Number(r.amount) || 0),
        isPercent: r.isPercent,
        quoteElement: r.quoteElement,
        active: r.active,
      }),
    ),
  };
}

/** Required before saving, as the membership form: a name, and every line picked. */
export function quotePresetErrors(f: QuotePresetForm): string[] {
  const errors: string[] = [];
  if (!f.name.trim()) errors.push('Name is required');
  if (f.procedures.some((p) => !p.procSubID)) errors.push('All procedures must have a procedure selected');
  if (f.products.some((p) => !p.costItemId)) errors.push('All products must have a product selected');
  if (f.discounts.some((d) => !d.discountId)) errors.push('All discounts must have a discount selected');
  return errors;
}

async function loadOptions(): Promise<Options> {
  const settle = <T,>(p: Promise<T>, fallback: T) => p.catch(() => fallback);
  const [procedureTree, hourBands, products, discounts] = await Promise.all([
    settle(listProcedureTree(), []),
    settle(getQuotePresetHourBands(), []),
    settle(getProductCatalog(), []),
    settle(getDiscountCatalog(), []),
  ]);
  return {
    procedureTree,
    hourBands: hourBands.filter((b) => b > 0),
    products: [...(products ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    discounts: [...(discounts ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
  };
}

// ─── Modal ─────────────────────────────────────────────────────────────────────

export interface QuotePresetFormModalProps {
  /** Null to add one to `category`. */
  preset: QuotePreset | null;
  /** Where a new preset goes (and the name shown for an existing one's category). */
  category: { id: number; name: string } | null;
  onClose: () => void;
  onSaved: (saved: QuotePreset, isNew: boolean) => void;
}

export function QuotePresetFormModal({ preset, category, onClose, onSaved }: QuotePresetFormModalProps) {
  const isNew = preset === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<QuotePresetForm>(() => formFrom(preset, null, null), { tracking: !isNew });
  const { form: f } = t;
  const [options, setOptions] = useState<Options | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [saving, setSaving] = useState(false);
  // New lines open; saved ones start closed.
  const [open, setOpen] = useState<Set<string>>(new Set());

  // The pickers, then (for an edit) the preset's quote, before anything shows.
  const quoteId = preset?.quoteId ?? null;
  const resetForm = t.reset;
  useEffect(() => {
    let cancelled = false;
    const quote: Promise<Quote | null> = quoteId
      ? getQuote(quoteId).catch((err) => {
          // The quote behind it is gone: the preset holds nothing, as in legacy.
          if (isAxiosError(err) && err.response?.status === 404) return null;
          throw err;
        })
      : Promise.resolve(null);
    Promise.all([loadOptions(), quote])
      .then(([opts, q]) => {
        if (cancelled) return;
        setOptions(opts);
        resetForm(formFrom(preset, q, opts));
        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });
    return () => {
      cancelled = true;
    };
    // Loaded once per preset; `preset` is the row the modal opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, resetForm]);

  const ready = state === 'ready' && options !== null;
  const errors = quotePresetErrors(f);
  const missingName = !f.name.trim();
  const nameState: FieldState = missingName ? 'error' : 'none';

  function toggleOpen(key: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function addRow<K extends (typeof LISTS)[number]>(list: K, row: QuotePresetForm[K][number]) {
    t.patch({ [list]: [...f[list], row] } as Partial<QuotePresetForm>);
    setOpen((prev) => new Set(prev).add(row.key));
  }

  function patchRow<K extends (typeof LISTS)[number]>(list: K, key: string, next: Partial<QuotePresetForm[K][number]>) {
    t.patch({ [list]: f[list].map((r) => (r.key === key ? { ...r, ...next } : r)) } as Partial<QuotePresetForm>);
  }

  function removeRow(list: (typeof LISTS)[number], key: string) {
    t.patch({ [list]: (f[list] as { key: string }[]).filter((r) => r.key !== key) } as Partial<QuotePresetForm>);
  }

  /** A procedure's fields; picking one also brings its own anesthesia and OR fees. */
  function patchProcedure(key: string, next: Partial<PresetProcedureRow>) {
    if (next.procSubID === undefined || !options) {
      patchRow('procedures', key, next);
      return;
    }
    const picked = options.procedureTree
      .flatMap((c) => c.subcategories.flatMap((s) => s.procedures))
      .find((p) => p.procSubID === next.procSubID);
    patchRow('procedures', key, picked ? { ...next, anesthesiaFee: picked.anesthesiaFee ?? 0, operatingRoomFee: picked.operatingRoomFee ?? 0 } : next);
  }

  async function handleSave() {
    if (errors.length > 0 || saving || !ready) return;
    if (preset && !t.dirty) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const content = contentPayload(f);
      if (preset) {
        const changes: Parameters<typeof updateQuotePreset>[1] = {};
        if (t.changed.has('name')) changes.name = f.name.trim();
        if (t.changed.has('procedures')) changes.procedures = content.procedures;
        if (t.changed.has('products')) changes.items = content.items;
        if (t.changed.has('discounts')) changes.discounts = content.discounts;
        const saved = await updateQuotePreset(preset.quoteTemplateId, changes);
        toast.success('Quote preset updated');
        onSaved(saved, false);
      } else {
        if (!category) return;
        const saved = await createQuotePreset({ name: f.name.trim(), quoteTemplateCategoryId: category.id, ...content });
        toast.success('Quote preset created');
        onSaved(saved, true);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, 'The quote preset could not be saved. Try again. If it keeps happening, contact Prospera support.'));
    } finally {
      setSaving(false);
    }
  }

  const where = category ? `In ${category.name}. ` : '';
  const description = locked
    ? where.trim() || undefined
    : `${where}What Load Preset puts on a patient's quote. Name is required.`;

  const productCategories = options ? [...new Set(options.products.map(categoryOf))].sort().map((c) => ({ value: c, label: c })) : [];

  return (
    <AdminFormModal
      title={locked ? 'Quote Preset' : `${isNew ? 'Create' : 'Edit'} Quote Preset`}
      description={description}
      isNew={isNew}
      dirty={t.dirty}
      saving={saving}
      errors={ready ? errors : []}
      canSave={ready}
      onSave={() => void handleSave()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="xl"
    >
      {state === 'failed' ? (
        <p className="py-12 text-center text-sm text-slate-500">This preset couldn't be loaded. Close and reopen it to try again.</p>
      ) : !ready ? (
        <SpinnerOverlay label="Loading preset…" />
      ) : (
        <div className="space-y-4">
          <DialogField
            label="Name"
            htmlFor="quote-preset-name"
            required
            state={nameState}
            labelHint={missingName ? '— required' : undefined}
            badge={t.badge('name', 'Name')}
          >
            <Input
              id="quote-preset-name"
              value={f.name}
              maxLength={150}
              placeholder="Preset name"
              onChange={(e) => t.patch({ name: e.target.value })}
              className={fieldClasses.input(nameState)}
            />
          </DialogField>

          {/* Procedures */}
          <ItemsBox
            label="Procedures"
            addLabel="Add procedure"
            badge={t.badge('procedures', 'Procedures')}
            onAdd={() =>
              addRow('procedures', {
                key: nextKey(),
                planProcedureId: null,
                categoryID: null,
                procID: null,
                procSubID: null,
                savedName: '',
                savedCategoryName: '',
                savedSubcategoryName: '',
                description: '',
                hours: 0,
                price: '0',
                effectivePrice: '0',
                anesthesiaFee: 0,
                operatingRoomFee: 0,
              })
            }
          >
            {f.procedures.map((row) => {
              const name = procedureNameOf(row, options.procedureTree);
              return (
                <ItemRow
                  key={row.key}
                  title={name}
                  missing="(procedure required)"
                  // Legacy's row header shows the effective price.
                  meta={row.procSubID ? money(Number(row.effectivePrice) || 0) : null}
                  open={open.has(row.key)}
                  onToggle={() => toggleOpen(row.key)}
                  removeLabel={`Remove procedure ${name ?? ''}`.trim()}
                  onRemove={() => removeRow('procedures', row.key)}
                >
                  <ProcedureFields
                    row={row}
                    tree={options.procedureTree}
                    hourBands={options.hourBands}
                    onPatch={(next) => patchProcedure(row.key, next)}
                  />
                </ItemRow>
              );
            })}
          </ItemsBox>

          {/* Products */}
          <ItemsBox
            label="Products"
            addLabel="Add product"
            badge={t.badge('products', 'Products')}
            onAdd={() =>
              addRow('products', {
                key: nextKey(),
                quoteItemId: null,
                costItemId: null,
                category: '',
                itemName: '',
                itemType: '',
                unit: '',
                description: '',
                curRate: '0',
                quantity: '1',
                taxable: false,
              })
            }
          >
            {f.products.map((row) => {
              const catalog = options.products.find((p) => p.costItemId === row.costItemId);
              const title = row.costItemId ? catalog?.name || row.itemName || 'Product' : null;
              const choices = options.products
                .filter((p) => !row.category || categoryOf(p) === row.category)
                .map((p) => ({ value: p.costItemId, label: p.name }));
              if (row.costItemId && !catalog) choices.push({ value: row.costItemId, label: `${row.itemName || 'Product'} (inactive)` });
              const quantity = Math.max(1, Math.round(Number(row.quantity) || 1));
              return (
                <ItemRow
                  key={row.key}
                  title={title}
                  missing="(product required)"
                  meta={`Qty: ${quantity}`}
                  aside={row.costItemId ? money((Number(row.curRate) || 0) * quantity) : null}
                  open={open.has(row.key)}
                  onToggle={() => toggleOpen(row.key)}
                  removeLabel={`Remove product ${title ?? ''}`.trim()}
                  onRemove={() => removeRow('products', row.key)}
                >
                  {/* As the membership product: Category, Product and the Description down three quarters; Cost, Quantity, Taxable beside them. */}
                  <div className="grid gap-3 md:grid-cols-4">
                    <div className="min-w-0 space-y-3 md:col-span-3">
                      <DialogField label="Category" htmlFor={`${row.key}-category`}>
                        <SearchableCombobox<string>
                          id={`${row.key}-category`}
                          options={productCategories}
                          value={row.category || null}
                          onChange={(v) => patchRow('products', row.key, { category: v ?? '' })}
                          placeholder="Select category"
                          clearable
                        />
                      </DialogField>
                      <DialogField label="Product" htmlFor={`${row.key}-product`} state={row.costItemId ? 'none' : 'error'}>
                        <SearchableCombobox<number>
                          id={`${row.key}-product`}
                          options={choices}
                          value={row.costItemId}
                          onChange={(v) => {
                            if (v === row.costItemId) return;
                            const picked = options.products.find((p) => p.costItemId === v);
                            patchRow('products', row.key, {
                              costItemId: v ?? null,
                              ...(picked
                                ? {
                                    category: categoryOf(picked),
                                    itemName: picked.name,
                                    unit: picked.unit,
                                    curRate: String(picked.defaultRate),
                                    taxable: picked.taxable,
                                  }
                                : {}),
                            });
                          }}
                          placeholder="Select product"
                        />
                      </DialogField>
                      <DialogField label="Description" htmlFor={`${row.key}-description`}>
                        <Textarea
                          autoResize
                          id={`${row.key}-description`}
                          rows={2}
                          value={row.description}
                          onChange={(e) => patchRow('products', row.key, { description: e.target.value })}
                        />
                      </DialogField>
                    </div>
                    <div className="min-w-0 space-y-3">
                      <DialogField label="Cost" htmlFor={`${row.key}-cost`}>
                        <Input
                          id={`${row.key}-cost`}
                          type="number"
                          step="0.01"
                          min="0"
                          leftAddon="$"
                          value={row.curRate}
                          onChange={(e) => patchRow('products', row.key, { curRate: e.target.value })}
                        />
                      </DialogField>
                      <DialogField label="Quantity" htmlFor={`${row.key}-quantity`}>
                        <Input
                          id={`${row.key}-quantity`}
                          type="number"
                          min="1"
                          value={row.quantity}
                          onChange={(e) => patchRow('products', row.key, { quantity: e.target.value })}
                          onBlur={() => {
                            if (!(Number(row.quantity) >= 1)) patchRow('products', row.key, { quantity: '1' });
                          }}
                        />
                      </DialogField>
                      <DialogField label="Taxable" htmlFor={`${row.key}-taxable`}>
                        <Select
                          id={`${row.key}-taxable`}
                          options={[
                            { value: '0', label: 'No' },
                            { value: '1', label: 'Yes' },
                          ]}
                          value={row.taxable ? '1' : '0'}
                          onChange={(v) => patchRow('products', row.key, { taxable: v === '1' })}
                        />
                      </DialogField>
                    </div>
                  </div>
                </ItemRow>
              );
            })}
          </ItemsBox>

          {/* Discounts */}
          <ItemsBox
            label="Discounts"
            addLabel="Add discount"
            badge={t.badge('discounts', 'Discounts')}
            onAdd={() =>
              addRow('discounts', {
                key: nextKey(),
                quoteDiscountId: null,
                discountId: null,
                name: '',
                description: '',
                amount: '0',
                isPercent: DEFAULT_DISCOUNT_IS_PERCENT,
                quoteElement: DEFAULT_DISCOUNT_SCOPE,
                active: true,
              })
            }
          >
            {f.discounts.map((row) => {
              const catalog = options.discounts.find((d) => d.discountId === row.discountId);
              const title = row.discountId ? row.name || catalog?.name || 'Discount' : null;
              const choices = options.discounts.map((d) => ({ value: d.discountId, label: d.name }));
              if (row.discountId && !catalog) choices.push({ value: row.discountId, label: `${row.name || 'Discount'} (inactive)` });
              const amount = Number(row.amount) || 0;
              return (
                <ItemRow
                  key={row.key}
                  title={title}
                  missing="(discount required)"
                  meta={row.discountId ? (row.isPercent ? `${amount}%` : money(amount)) : null}
                  aside={row.discountId ? (DISCOUNT_SCOPE_OPTIONS.find((s) => s.value === row.quoteElement)?.label ?? null) : null}
                  open={open.has(row.key)}
                  onToggle={() => toggleOpen(row.key)}
                  removeLabel={`Remove discount ${title ?? ''}`.trim()}
                  onRemove={() => removeRow('discounts', row.key)}
                >
                  {/* The product row's layout: the Discount and its Description, then Amount, Type and Applies to beside them. */}
                  <div className="grid gap-3 md:grid-cols-4">
                    <div className="min-w-0 space-y-3 md:col-span-3">
                      <DialogField label="Discount" htmlFor={`${row.key}-discount`} state={row.discountId ? 'none' : 'error'}>
                        <SearchableCombobox<number>
                          id={`${row.key}-discount`}
                          options={choices}
                          value={row.discountId}
                          onChange={(v) => {
                            if (v === row.discountId) return;
                            const picked = options.discounts.find((d) => d.discountId === v);
                            // The catalog seeds the line; each field is then the preset's own.
                            patchRow('discounts', row.key, {
                              discountId: v ?? null,
                              ...(picked
                                ? {
                                    name: picked.name,
                                    description: picked.description,
                                    amount: String(picked.amount),
                                    isPercent: picked.isPercent,
                                    quoteElement: picked.quoteElement || DEFAULT_DISCOUNT_SCOPE,
                                  }
                                : {}),
                            });
                          }}
                          placeholder="Select discount"
                        />
                      </DialogField>
                      <DialogField label="Description" htmlFor={`${row.key}-description`}>
                        <Textarea
                          autoResize
                          id={`${row.key}-description`}
                          rows={2}
                          value={row.description}
                          onChange={(e) => patchRow('discounts', row.key, { description: e.target.value })}
                        />
                      </DialogField>
                    </div>
                    <div className="min-w-0 space-y-3">
                      <DialogField label="Amount" htmlFor={`${row.key}-amount`}>
                        <Input
                          id={`${row.key}-amount`}
                          type="number"
                          step="0.01"
                          min="0"
                          leftAddon={row.isPercent ? undefined : '$'}
                          rightAddon={row.isPercent ? '%' : undefined}
                          value={row.amount}
                          onChange={(e) => patchRow('discounts', row.key, { amount: e.target.value })}
                        />
                      </DialogField>
                      <DialogField label="Type" htmlFor={`${row.key}-type`}>
                        <Select
                          id={`${row.key}-type`}
                          options={DISCOUNT_TYPE_OPTIONS.map((o) => ({ value: o.isPercent ? '1' : '0', label: o.label }))}
                          value={row.isPercent ? '1' : '0'}
                          onChange={(v) => patchRow('discounts', row.key, { isPercent: v === '1' })}
                        />
                      </DialogField>
                      <DialogField label="Applies to" htmlFor={`${row.key}-scope`}>
                        <Select
                          id={`${row.key}-scope`}
                          options={DISCOUNT_SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
                          value={row.quoteElement}
                          onChange={(v) => v && patchRow('discounts', row.key, { quoteElement: v })}
                        />
                      </DialogField>
                    </div>
                  </div>
                </ItemRow>
              );
            })}
          </ItemsBox>
        </div>
      )}
    </AdminFormModal>
  );
}
